<template lang="pug">
    .play-box
        template(v-if='!isLoading')
            game-preparation-counter(v-if='!gameStarted' :counter="counter")
            template(v-else="")
                game-header(
                    :time='getTime'
                    :slug="type"
                    :gameType="parameters.courseType"
                    @soundStateChanged ="headerKey = !headerKey"
                    :key="headerKey"
                    :is-homework="!!exerciseId"
                )
                .play-box__game(v-if='gameStarted && !gameFinished || showUnselectedWords')
                    .play-box__example
                        .play-box__header
                            .play-box__title {{ $i18n.t('trainer.trainer_game_synonyms_findSynonyms') }}
                            .play-box__word.play-box__subtitle(
                                v-if="example",
                                :style="'font-size:' + fontSize + 'px; line-height:' + (+fontSize + 3) + 'px'"
                            )
                                | {{ example.word }}
                        scaling-bar(
                            @setFontSize="setFontSize"
                            :is-game-tuned="true"
                            :font-size="fontSize"
                            element-name="play-box__field"
                            :is-from-shutter="false"
                            :key="clientWidth"
                        )
                        .play-box__overflow-container
                            .play-box__field
                                span.play-box__word(
                                    v-for="word in displayedWords"
                                    @click="selectWord(word)"
                                    :style="'font-size:' + fontSize + 'px; line-height:' + (+fontSize + 3) + 'px'"
                                    :class="{\
                                        'play-box__word--selected': isWordSelected(word),\
                                        'play-box__word--selected-unwanted': isUnwantedWordSelected(word) && !isOlympiad,\
                                        'play-box__word--unselected-needed': isNeededWordUnselected(word) && !isOlympiad\
                                    }"
                                )
                                    | {{ word.word }}
                        .play-box__buttons
                            cs-button.play-box__button(
                                v-if="!showUnselectedWords"
                                :disabled="selectedWords.length === 0"
                                @click="processReply"
                                :style="'font-size:' + fontSize + 'px; line-height:' + (+fontSize + 3) + 'px'"
                            )
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_reply') }}
                            cs-button.play-box__button(
                                v-else=""
                                @click="showNextExample"
                                :style="'font-size:' + fontSize + 'px; line-height:' + (+fontSize + 3) + 'px'"
                            )
                                | {{ $i18n.t('olympiads.olympiads_processStage_further') }}
                    game-info.play-box__game-info(
                        :score="{left: answers.good, right: answers.bad}"
                        :time="getTime"
                        :is-olympiad="isOlympiad"
                    )
                .play-box__end-container(v-if='gameFinished && !isOlympiad && !showUnselectedWords')
                    fireworks(
                        v-if="gameFinished && !isOlympiad"
                        :is-mobile="isMobile"
                    )
                    .play-box__reaction-container
                        .play-box__time
                            | {{ timeExecutionString }}
                        result-image.play-box__image(status='success')
                    result-buttons(
                        :exercise-id="exerciseId"
                        :games-type="parameters.courseType"
                        show-restart-button
                        @restartGame="restartGame"
                    )
        cs-loader(v-else)
</template>

<script>
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import olympiadAnswerTimerMixin from '../../../../utils/olympiads/olympiadAnswerTimerMixin.js';
    import textScrollMixin from '../../../../utils/trainer/textScroll.js';
    import SynonymsApi from '../../../../api/games/synonymsRussianLanguage.js';

    import ScalingBar from './ScalingBar.vue';
    import GameInfo from '../GameInfo.vue';
    import GamePreparationCounter from '../../GamePreparationCounter.vue';

    const AVAILABLE_LANGUAGES_FOR_SYNONYMS_ONLINE = ['ru', 'uk'];
    const AVAILABLE_LANGUAGES_FOR_SYNONYMS = ['ru', 'uk'];

    function DEFAULT_DATA() {
        return {
            examples: [],
            example: null,
            fontSize: 18,
            amountOfWordsInRow: 1,
            showUnselectedWords: false,
            neededWords: [],
            selectedWords: [],
            answers: {
                good: 0,
                bad: 0,
            },
            clientWidth: 0,
        };
    }

    export default {
        components: { GamePreparationCounter, GameInfo, ScalingBar },
        mixins: [textScrollMixin, playBoxGameMixin, olympiadAnswerTimerMixin],
        created () {
            window.addEventListener('resize', this.getClientWidth);
        },
        mounted() {
            this.gameBoxName = 'play-box__example';
            this.getClientWidth();
        },
        data: DEFAULT_DATA,
        computed: {
            showGameHeader() {
                return !this.gameFinished && this.gameStarted;
            },
            locale() {
                if (this.user && this.user.locale) {
                    if (this.type === 'SynonymsOnline') {
                        return AVAILABLE_LANGUAGES_FOR_SYNONYMS_ONLINE.includes(this.user.locale)
                            ? this.user.locale
                            : 'ru';
                    }
                    return AVAILABLE_LANGUAGES_FOR_SYNONYMS.includes(this.user.locale) ? this.user.locale : 'ru';
                }
                return 'ru';
            },
            amountOfWordsToFind() {
                return this.example.synonyms.length;
            },
            displayedWords() {
                return _.shuffle(this.example.answerWords);
            },
            timeExecutionString() {
                if (!this.isOlympiad) {
                    return (
                        this.$i18n.t('progress.progress_table_executionTime') +
                        ': ' +
                        this.time +
                        ' ' +
                        this.$i18n.tc('olympiads.olympiads_timeDeclination_seconds', this.time)
                    );
                }
                return '';
            },
            isOnline() {
                return this.type === 'SynonymsOnline';
            },
        },
        methods: {
            getClientWidth() {
                this.clientWidth = document.documentElement.clientWidth;
                if (this.clientWidth < 576 && this.fontSize > 30) {
                    this.setFontSize(30);
                } else if (this.clientWidth < 766 && this.fontSize > 40) {
                    this.setFontSize(40);
                } else if (this.clientWidth < 1200 && this.fontSize > 60) {
                    this.setFontSize(60);
                } else if (this.clientWidth < 1430 && this.fontSize > 80) {
                    this.setFontSize(80);
                } else if (this.clientWidth < 1920 && this.fontSize > 100) {
                    this.setFontSize(100);
                } else if (this.clientWidth >= 1920){
                    this.setFontSize(150);
                }
            },
            generateExamples(amountOfExamples) {
                return new Promise((resolve, reject) => {
                    SynonymsApi.getSynonymsByParams({
                        amountOfExamples,
                        locale: this.locale,
                        amountOfVariants: this.parameters.amountOfVariants,
                        forOnlineGame: this.isOnline,
                    })
                        .then(data => {
                            this.examples = data.examples;
                            this.example = this.examples.shift();
                            this.neededWords = this.example.synonyms;
                            resolve();
                        })
                        .catch(e => {
                            reject(e);
                        });
                });
            },
            fetchData() {
                this.getExercise()
                    .then(() => {
                        let examplesLeft = this.parameters.amountOfExamples - this.answers.good;
                        this.generateExamples(examplesLeft)
                            .then(() => {
                                this.registerSounds();
                            })
                            .catch(e => this.showErrorMessage(e));
                    })
                    .catch(() => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            beforeStart() {
                this.startGame();
            },
            startGame() {
                this.startTimer();
                if (this.answerDuration) {
                    this.startAnswerTimer();
                }
                this.gameStarted = true;
            },
            finishGame() {
                this.gameFinished = true;
                this.stopTimer();
                if (!this.isOnline) {
                    this.sendStat();
                }
            },
            setFontSize(fontSize) {
                this.fontSize = fontSize;
            },
            startTimer() {
                this.$nextTick(() => {
                    if (!this.timer) {
                        this.setTimer();
                    }
                });
            },
            selectWord(word) {
                if (!this.gameFinished && !this.showUnselectedWords) {
                    let index = this.selectedWords.findIndex(element => _.isEqual(element, word));
                    if (index !== -1) {
                        this.selectedWords.splice(index, 1);
                    } else {
                        if (this.selectedWords.length === this.amountOfWordsToFind) {
                            return;
                        }
                        this.selectedWords.push(word);
                    }
                    window.addEventListener('keydown', this.processReplyOnEnter);
                }
            },
            processReply() {
                this.stopTimer();
                this.stopAnswerTimer();
                let neededWords = this.example.synonyms.map(item => {
                    return { id: item.id, word: item.word };
                });
                let selectedWords = this.selectedWords.map(element => {
                    return { id: element.id, word: element.word };
                });
                let difference = _.differenceBy(neededWords, selectedWords, 'word');

                if (difference.length === 0) {
                    this.answers.good++;
                    this.playResultSound('success');
                    this.showNextExample();
                } else {
                    this.answers.bad++;
                    this.playResultSound('failure');
                    if (this.isOlympiad) {
                        this.showNextExample();
                    } else {
                        this.showUnselectedWords = true;
                        window.addEventListener('keydown', this.showNextExample);
                    }
                }
                window.removeEventListener('keydown', this.processReplyOnEnter);
            },
            playResultSound(result) {
                if (!this.isOlympiad) {
                    this.resultInstance = this.$createjs.Sound.play(result);
                    this.resultInstance.volume = 0.6;
                }
            },
            refreshGameStatBeforeSend() {
                this.gameStat.attempts = this.answers.good + this.answers.bad;
                this.gameStat.answers = this.answers.good;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
                this.gameStat.parameters = _.cloneDeep(this.parameters);
            },
            calculatePercent() {
                let total = this.answers.good + this.answers.bad;
                if (total > 0) {
                    return parseInt((this.answers.good / total) * 100);
                }
                return 0;
            },
            showNextExample() {
                this.selectedWords = [];
                this.showUnselectedWords = false;
                window.removeEventListener('keydown', this.showNextExample);
                if (!this.$offline && (this.user.type === 'student' || this.user.type === 'olympiads-student')) {
                    this.sendStat();
                }
                if (this.isOlympiad && this.answers.good + this.answers.bad >= this.parameters.amountOfExamples) {
                    this.gameFinished = true;
                    return;
                }
                if (this.examples.length) {
                    this.example = this.examples.shift();
                    this.neededWords = this.example.synonyms;
                    this.setTimer();
                    if (this.answerDuration) {
                        this.startAnswerTimer();
                    }
                } else if (this.answers.good < this.parameters.amountOfExamples) {
                    let examplesLeft = this.parameters.amountOfExamples - this.answers.good;
                    this.generateExamples(examplesLeft);
                } else {
                    this.finishGame();
                }
            },
            processReplyOnEnter(event) {
                if (event.keyCode === 13) {
                    this.processReply();
                }
            },
            isWordSelected(word) {
                return this.selectedWords.some(element => {
                    return this.areWordsEqual(word, element);
                });
            },
            areWordsEqual(first, second) {
                return first.id === second.id || first.word.trim() === second.word.trim();
            },
            isNeededWordUnselected(word) {
                if (this.showUnselectedWords) {
                    return this.neededWords.some(item => {
                        if (this.areWordsEqual(item, word) && !this.isWordSelected(word)) {
                            return true;
                        }
                    });
                }
            },
            isUnwantedWordSelected(word) {
                if (this.showUnselectedWords) {
                    let isWordNeeded = false;
                    this.neededWords.forEach(item => {
                        if (this.areWordsEqual(item, word)) {
                            isWordNeeded = true;
                        }
                    });
                    return !isWordNeeded && this.isWordSelected(word);
                }
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
        },
        beforeDestroy() {
            window.removeEventListener('keydown', this.processReplyOnEnter);
            window.removeEventListener('keydown', this.showNextExample);
            window.removeEventListener('resize', this.getClientWidth);
        },
        comments: {
            ScalingBar,
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .play-box--warning {
        display: table;
    }

    .play-box__game {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column-reverse;
        @include media($md) {
            flex-direction: row;
        }
    }

    .play-box__example {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        flex-grow: 5;
        flex-direction: column;
    }

    .play-box__overflow-container {
        overflow-y: auto;
        @include scrollbar;
    }

    .play-box__field {
        padding: 0 20px;
        text-align: center;
        font-size: 18px;
        user-select: none;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    .play-box__word {
        padding: 10px;
        margin: 0 10px 10px 0;
        background: $x-color-white;
        border-radius: $border-radius-5;

        word-break: break-all;

        cursor: pointer;
    }

    .play-box__word:last-child {
        margin-right: 0;
    }

    .play-box__word--selected {
        color: $x-color-white;
        background: $hsl-color-green-58;
    }

    .play-box__word--selected-unwanted {
        color: $x-color-white;
        background: $hsl-color-red-62;
    }

    .play-box__word--unselected-needed {
        color: $x-color-black;
        border: 1px solid $hsl-color-red-62;
        background: $x-color-white;
    }

    .play-box__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        margin-bottom: 20px;
        @include media($sm) {
            margin-bottom: 30px;
        }
        @include media($md) {
            font-size: 32px;
            line-height: 38px;
        }
    }

    .play-box__title {
        margin-bottom: 20px;
    }

    .play-box__subtitle {
        margin-bottom: 0;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
    }

    .play-box__time {
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
        text-align: center;
        color: $hsl-color-green-58;
        margin-bottom: 30px;
    }

    .play-box__answers {
        font-weight: 500;
        font-size: 18px;
        line-height: 21px;
        text-align: center;
        @include media($md) {
            font-size: 22px;
            line-height: 26px;
        }
        @include media($lg) {
            font-size: 32px;
            line-height: 38px;
        }
        @include media($xl) {
            font-size: 42px;
            line-height: 50px;
        }
    }

    .play-box__image {
        max-width: 100%;
        margin-bottom: 30px;
        @include media($md) {
            margin-bottom: 40px;
        }
    }

    .play-box__buttons {
        display: flex;
        flex-direction: column;
        margin-top: 20px;
        @include media($sm) {
            margin-top: 25px;
        }
        @include media($lg) {
            flex-direction: row;
        }
    }

    .play-box__button {
        margin: 20px 0;
        @include media($lg) {
            margin: 0 20px;
        }
    }

    .play-box__button:first-child {
        margin-top: 0;
        @include media($lg) {
            margin-left: 0;
        }
    }

    .play-box__button:last-child {
        margin-bottom: 0;
        @include media($lg) {
            margin-right: 0;
        }
    }

    .play-box__game-info {
        align-self: flex-end;
        @include media($md) {
            align-self: auto;
        }
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }
</style>
